import { Injectable } from '@angular/core';
import { MapComponent, Point } from 'app/shared/components';
import { getIconSvgString } from 'app/shared/components/map/map.markers';
import { TelemetryResponse, IdNameType, EventMedia } from '@key-telematics/fleet-api-client';

import * as moment from 'moment-timezone';
import { debounceTime } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { MapService } from './map.service';
import { AppService } from 'app/app.service';
import { flatten } from 'lodash';

export interface MapEventItem {
    id: string;
    owner: IdNameType;
    date: string;
    class: string;
    type: string;
    details: { [key: string]: any; };
    alerts: IdNameType[];
    media?: EventMedia[];
    thumbnail?: string;
}


export interface MapTelemetry extends TelemetryResponse {
    events?: MapEventItem[];
}

@Injectable()
export class MapTripService extends MapService {
    public telemetryTrips: MapTelemetry[][] = [];

    public waypoints: MapTelemetry[] = [];
    public selectedTelemetryIndex = 0;
    public playback$ = new Subject<'playing' | 'stopped'>();
    public playbackTimer: number;
    public selectedTelemetry$ = new Subject<MapTelemetry>();

    protected asset: { id: string, color: string };
    protected waypointZoomLevel: number;

    private movement$: Subscription;
    private markerProximity = 25;
    private polylines: string[] = [];

    constructor(app: AppService) {
        super(app);
    }

    public setTripsTelemetry(trips: { telemetry: TelemetryResponse[], events?: MapEventItem[] }[]) {

        if (this.map) {
            this.telemetryTrips = [];
            setTimeout(() => {
                this.telemetryTrips = (trips || []).map(trip => {
                    return (trip.telemetry || []).map(tel => ({
                        ...tel,
                        events: trip.events ? trip.events.filter(e => e.date === tel.date) : [],
                    }));
                });
                this.redrawWaypoints();
            });
        }
    }

    public redrawWaypoints(reload = true) {
        if (!this.map) { return; }

        if (!reload && this.map.getZoom() === this.waypointZoomLevel) {
            return;
        }

        if (!this.telemetryTrips || this.telemetryTrips.length === 0) {
            return;
        }

        this.clearPolylines();

        // separate line segments per trip and break each one's telemetry up by asset state color
        const groups: { color: string, points: Point[] }[][] = [];
        this.telemetryTrips.forEach((trip, index) => {
            groups[index] = [];
            let group = null;
            let prevColor = '';
            trip.forEach(tel => {
                if (tel.location) {
                    const state = tel.state && tel.state[Object.keys(tel.state)[0]];
                    const color = (state && state.color) || this.asset.color;
                    if (prevColor !== color) {
                        if (group) {
                            // add this position to the last group too to complete the line
                            group.points.push(this.latLng(tel));
                        }
                        group = { points: [], color: color };
                        groups[index].push(group);
                    }
                    group.points.push(this.latLng(tel));
                    prevColor = color;
                }
            });
        });

        groups.forEach((tripGroup, index) => {
            tripGroup.forEach((g, i) => {
                const id = `trip-path-${index}-${i}`;
                this.polylines.push(id);
                this.map.addPolyline(id, g.points, { color: g.color, weight: 6, opacity: 0.5 });
            });
        });

        if (reload) {
            this.map.zoomToBounds(this.map.getPointBounds(flatten(this.telemetryTrips.map(trip => trip.map(this.latLng)))));
        }

        this.waypointZoomLevel = this.map.getZoom();
        this.waypoints = flatten(this.telemetryTrips.map(trip => this.deduplicateTelemetry(trip)));
        this.waypoints.forEach((val, index) =>
            this.drawMarker(val, index)
        );

    }

    clearPolylines() {
        this.polylines.forEach(id => {
            this.map.removePolyline(id);
        });
        this.polylines = [];
    }

    attachMap(mapComponent: MapComponent) {
        this.detachMap();
        this.map = mapComponent;
        this.movement$ = mapComponent.onMapMoved
            .pipe(
                debounceTime(100)
            )
            .subscribe(() => this.redrawWaypoints(false));
    }

    detachMap() {
        if (this.movement$) {
            this.movement$.unsubscribe();
        }

        this.reset();
        this.map = null;
    }

    setAsset(asset: { id: string, color: string }) {
        this.asset = asset;
    }

    stopPlayback() {
        clearInterval(this.playbackTimer);
        this.playback$.next('stopped');
    }

    startPlayback() {
        if (this.selectedTelemetryIndex >= this.waypoints.length - 1) {
            this.selectedTelemetryIndex = 0;
        }

        this.playbackTimer = setInterval(this.seekForward.bind(this), 1000) as any;
        this.playback$.next('playing');
    }

    seekBackward() {
        this.selectedTelemetryIndex--;

        if (this.selectedTelemetryIndex < 0 && this.playbackTimer) {
            this.stopPlayback();
            this.selectedTelemetryIndex = 0;
        } else {
            this.selectWaypointIndex(this.selectedTelemetryIndex);
        }
    }

    seekForward() {
        if (this.selectedTelemetryIndex >= this.waypoints.length && this.playbackTimer) {
            this.stopPlayback();
            this.selectedTelemetryIndex = this.waypoints.length - 1;
        } else {
            this.selectWaypointIndex(this.selectedTelemetryIndex);
        }

        this.selectedTelemetryIndex++;
    }

    seekStart() {
        this.selectedTelemetryIndex = 0;
        this.selectWaypointIndex(this.selectedTelemetryIndex);
    }

    seekEnd() {
        this.selectedTelemetryIndex = this.waypoints.length - 1;
        this.selectWaypointIndex(this.selectedTelemetryIndex);
    }

    reset() {
        if (this.playbackTimer) {
            this.stopPlayback();
        }

        this.selectedTelemetryIndex = 0;
        this.telemetryTrips = [];
        this.waypoints = [];

        if (this.map) {
            this.clearPolylines();
            this.map.clearMarkers();
        }
    }

    selectWaypointIndex(index: number) {
        this.map.selectMarker(String(index), true);
        this.selectedTelemetry$.next(this.waypoints[index]);
    }

    selectWaypointTime(date: string) {
        if (this.waypoints.length === 0) { return; }

        const presentWaypointIndex = this.waypoints.findIndex(point => point.date === date);

        if (presentWaypointIndex > -1) {
            return this.selectWaypointIndex(presentWaypointIndex);
        }

        const dateTime = moment.utc(date).tz(moment.defaultZone.name);
        let insertIndex = this.waypoints.findIndex((point, index, all) => {
            const currentTime = moment.utc(point.date).tz(moment.defaultZone.name);

            if (index === all.length - 1) {
                return false;
            }

            const nextTime = moment.utc(all[index + 1].date).tz(moment.defaultZone.name);

            if (currentTime.isBefore(dateTime) && nextTime.isAfter(dateTime)) {
                return true;
            }
        });

        if (insertIndex === -1) {
            if (dateTime.isBefore(this.waypoints[0].date)) {
                insertIndex = 0;
            } else if (dateTime.isAfter(this.waypoints[this.waypoints.length - 1].date)) {
                insertIndex = this.waypoints.length;
            } else {
                throw new Error('The universe has broken 😰');
            }
        } else {
            insertIndex++;
        }

        const item = flatten(this.telemetryTrips).find(tel => tel.date === date);
        this.drawMarker(item, insertIndex);

        const start = this.waypoints.slice(0, insertIndex);
        const rest = this.waypoints.slice(insertIndex, this.waypoints.length);
        this.waypoints = start.concat([item]).concat(rest);

        this.selectWaypointIndex(insertIndex);
    }

    drawMarker(telemetry: MapTelemetry, index: number) {
        this.map.setMarker({
            id: String(index),
            lat: telemetry.location.lat,
            lon: telemetry.location.lon,
            getIcon: (selected) => {
                return telemetry.events[0] ? this.getEventIcon(telemetry.events[0].alerts, selected) : this.getMarkerIcon(telemetry, this.asset.color, selected ? 45 : 30);
            },
            click: () => {
                this.selectedTelemetryIndex = index;
                this.selectWaypointIndex(index);
            },
        });
    }

    deduplicateTelemetry(telemetries: TelemetryResponse[]) {
        if (!telemetries) {
            return [];
        }

        const waypoints = [];
        let prev: TelemetryResponse = null;

        for (let i = 0; i < telemetries.length; i++) {
            const current = telemetries[i];
            const proximity = prev ? this.map.getPixelDistance(this.latLng(prev), this.latLng(current)) : 10000;

            if (i === telemetries.length - 1 || proximity >= this.markerProximity) {
                waypoints.push(current);
                prev = current;
            }
        }

        return waypoints;
    }

    latLng(telemetry: TelemetryResponse): Point {
        return {
            y: telemetry.location.lat,
            x: telemetry.location.lon,
        };
    }

    getMarkerIcon(telemetry: TelemetryResponse, color: string, size: number) {
        let iconType = 'idle';

        if (telemetry.active) {
            if (!!telemetry.location && telemetry.location.speed === 0) {
                iconType = 'stop';
            } else {
                iconType = 'direction';
            }
        }

        return {
            size: size,
            html: getIconSvgString(iconType, {
                fill: color,
                size: size,
                rotation: telemetry.location.heading,
            }),
        };
    }

    getEventIcon(alerts: { type?: string, name?: string }[] = [], selected: boolean) {
        const alertType: { [key: string]: { classes: string, shape: 'circle' | 'triangle' } } = {
            low: { classes: 'icon-info-circle text-success', shape: 'circle' },
            medium: { classes: 'icon-exclamation-circle text-warning', shape: 'circle' },
            high: { classes: 'icon-exclamation-triangle text-danger', shape: 'triangle' },
        };

        // any non alert event returns 'low' which is a beautiful green information icon. We can always extend it later to return special icons for specific events like camera and images
        const type = alertType[(alerts.length === 0) ? 'low' : alerts[0].type || 'low'];

        return {
            size: 30,
            html: `
                <div class="map-font-icon ${selected ? 'selected' : ''}">
                    <i class="icon icon__fill ${type.classes}"></i>
                    <i class="icon icon__fill icon-${type.shape} map-font-icon__background map-font-icon__shadows"></i>
                </div>`,
        };
    }
}
